/** Display helpers. Pure functions, no rendering concerns. */

const UNITS = [
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'G' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'k' },
];

/**
 * Compact notation matching the style the game GUI uses for threat
 * (maps/biter_battles_v2/gui.lua:152).
 */
export function formatCompact(value: number, digits = 1): string {
  if (value === 0) return '0';
  const abs = Math.abs(value);
  for (const { limit, suffix } of UNITS) {
    if (abs >= limit) {
      const scaled = value / limit;
      const d = Math.abs(scaled) >= 100 ? 0 : digits;
      return `${scaled.toFixed(d)}${suffix}`;
    }
  }
  return abs >= 10 ? value.toFixed(0) : value.toFixed(abs >= 1 ? 1 : 2);
}

/** The scale suffixes `formatCompact` prints, smallest first. */
export const SCALES = [
  { key: '', label: '—', factor: 1 },
  { key: 'k', label: 'k', factor: 1e3 },
  { key: 'M', label: 'M', factor: 1e6 },
  { key: 'G', label: 'G', factor: 1e9 },
  { key: 'T', label: 'T', factor: 1e12 },
] as const;

export type ScaleKey = (typeof SCALES)[number]['key'];

/** Typed input is forgiving about case, and about "b" for billions. */
const SCALE_ALIAS: Record<string, ScaleKey> = {
  k: 'k',
  m: 'M',
  g: 'G',
  b: 'G',
  t: 'T',
};

export const scaleFactor = (key: ScaleKey): number =>
  SCALES.find((scale) => scale.key === key)?.factor ?? 1;

/**
 * Split a printed figure into the number and its scale suffix, so an editor can
 * offer them as two separate controls. Accepts "3.2M", "400k", "1,234",
 * "150.0%", "0.5". Returns null for anything it cannot read.
 */
export function splitCompact(text: string): { mantissa: number; scale: ScaleKey } | null {
  const cleaned = text.trim().replace(/,/g, '').replace(/%$/, '').replace(/\s+/g, '');
  const match = /^([+-]?(?:\d+\.?\d*|\.\d+))([kmgbt])?$/i.exec(cleaned);
  if (!match) return null;
  const mantissa = Number(match[1]);
  if (!Number.isFinite(mantissa)) return null;
  const suffix = match[2]?.toLowerCase();
  return { mantissa, scale: suffix ? SCALE_ALIAS[suffix] : '' };
}

/**
 * Inverse of `formatCompact`, so a figure can be read back in the notation it
 * was printed in. Callers treat null as "no change".
 */
export function parseCompact(text: string): number | null {
  const parts = splitCompact(text);
  return parts === null ? null : parts.mantissa * scaleFactor(parts.scale);
}

export function formatInt(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** Evolution as a percentage: 1.423 -> "142.3%". */
export function formatEvo(evo: number, digits = 1): string {
  return `${(evo * 100).toFixed(digits)}%`;
}

/** Multipliers as "2.50x". */
export function formatMultiplier(value: number, digits = 2): string {
  return `${value.toFixed(value >= 100 ? 0 : digits)}x`;
}

export function formatPercent(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Signed delta, for the "x => y" readouts. */
export function formatDelta(value: number, format: (n: number) => string): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${format(Math.abs(value))}`;
}

export function formatFlasks(count: number): string {
  if (count === 0) return '0';
  if (count < 100) return count.toFixed(1);
  return formatInt(count);
}
